import { Component, EventEmitter, Input, Output } from '@angular/core';
import { ConnectedPosition, OverlayModule } from '@angular/cdk/overlay';
import { A11yModule } from '@angular/cdk/a11y';
import { MatIconModule } from '@angular/material/icon';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'app-column-filter',
  templateUrl: './column-filter.component.html',
  styleUrls: ['./column-filter.component.less'],
  imports: [OverlayModule, A11yModule, MatIconModule, FormsModule],
  standalone: true
})
export class ColumnFilterComponent {
  @Input() header = '';
  @Input() options: string[] = [];
  @Input() selected: ReadonlySet<string> | null = null;
  @Output() applied = new EventEmitter<ReadonlySet<string>>();
  @Output() cleared = new EventEmitter<void>();

  isOpen = false;
  searchText = '';
  visibleOptions: string[] = [];
  draft = new Set<string>();

  readonly positions: ConnectedPosition[] = [
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 6 },
    { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 6 },
    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -6 },
  ];

  get isActive(): boolean {
    return this.selected !== null;
  }

  get checkedVisibleCount(): number {
    return this.visibleOptions.filter(option => this.draft.has(option)).length;
  }

  get allVisibleChecked(): boolean {
    return this.visibleOptions.length > 0 && this.checkedVisibleCount === this.visibleOptions.length;
  }

  get someVisibleChecked(): boolean {
    return this.checkedVisibleCount > 0;
  }

  open(event: Event): void {
    event.stopPropagation();
    this.searchText = '';
    this.visibleOptions = this.options;
    this.draft = new Set(this.selected ?? this.options);
    this.isOpen = true;
  }

  close(): void {
    this.isOpen = false;
  }

  onSearchChange(): void {
    const term = this.searchText.trim().toLowerCase();
    this.visibleOptions = term
      ? this.options.filter(option => this.getLabel(option).toLowerCase().includes(term))
      : this.options;
  }

  toggleOption(option: string): void {
    if (this.draft.has(option)) {
      this.draft.delete(option);
    } else {
      this.draft.add(option);
    }
  }

  toggleAllVisible(): void {
    if (this.allVisibleChecked) {
      this.visibleOptions.forEach(option => this.draft.delete(option));
    } else {
      this.visibleOptions.forEach(option => this.draft.add(option));
    }
  }

  clearSearch(searchInput: HTMLInputElement): void {
    this.searchText = '';
    this.onSearchChange();
    searchInput.focus();
  }

  apply(): void {
    if (!this.someVisibleChecked) return;
    this.applied.emit(new Set(this.visibleOptions.filter(option => this.draft.has(option))));
    this.close();
  }

  clear(): void {
    this.cleared.emit();
    this.close();
  }

  getLabel(option: string): string {
    return option === '' ? '(Blanks)' : option;
  }
}
